import {
  RECIPIENT_DRAWER_FLOW_CONFIG,
  RECIPIENT_PAYMENT_METHODS,
} from "./flowConfig";

import type { RecipientDrawerFlow, RecipientPaymentMethod } from "./flowConfig";

const RECIPIENT_METHOD_DESTINATION_KEY = {
  ach: "ach_destination",
  wire: "fedwire_destination",
  internationalWire: "swift_destination",
} as const satisfies Record<RecipientPaymentMethod, keyof API.Recipient>;

export const hasMethod = (
  recipient: API.Recipient,
  method: RecipientPaymentMethod,
) => Boolean(recipient[RECIPIENT_METHOD_DESTINATION_KEY[method]]);

// whether the recipient can be used in this flow without adding details first
export const isRequirementMet = (
  recipient: API.Recipient,
  flow: RecipientDrawerFlow,
) => {
  const { requiredMethod } = RECIPIENT_DRAWER_FLOW_CONFIG[flow];
  return !requiredMethod || hasMethod(recipient, requiredMethod);
};

// payment methods in display order, minus those the user doesn't have enabled
export const getVisibleMethods = (
  enabledMethods: Record<RecipientPaymentMethod, boolean>,
) => RECIPIENT_PAYMENT_METHODS.filter((method) => enabledMethods[method]);

// in payment flows, every method the flow doesn't require is labelled "(optional)"
export const isMethodOptional = (
  flow: RecipientDrawerFlow,
  method: RecipientPaymentMethod,
) => {
  const { requiredMethod } = RECIPIENT_DRAWER_FLOW_CONFIG[flow];
  return Boolean(requiredMethod) && requiredMethod !== method;
};

// the BIC and country lookups keep the bank's labels and fields in the international wire form; they aren't the user's input
const SWIFT_LOOKUP_FIELDS = new Set(["renameFields", "additionalFieldsToShow"]);

// react-hook-form's dirtyFields can keep `false` entries for fields a change put back
const hasChangedField = (dirty: unknown): boolean =>
  typeof dirty === "object" && dirty !== null
    ? Object.values(dirty).some(hasChangedField)
    : Boolean(dirty);

// whether the user has changed anything, given the international wire form's `formState.dirtyFields`
export const hasSwiftDestinationEdits = (dirtyFields: object) =>
  Object.entries(dirtyFields).some(
    ([name, dirty]) => !SWIFT_LOOKUP_FIELDS.has(name) && hasChangedField(dirty),
  );
